import React from "react";
import Select, { components as SelectComponents } from "react-select";
import AsyncSelect from "react-select/async";
import CreatableSelect from "react-select/creatable";
import { cn } from "@/lib/utils";
import { ChevronDown, X } from "lucide-react";

const ReactSelect = React.forwardRef(
  (
    {
      className,
      placeholder,
      options,
      value,
      onChange,
      isDisabled,
      error,
      classNames: customClassNames,
      ...props
    },
    ref,
  ) => {
    // Custom Dropdown Indicator using Lucide icon to match Shadcn
    const DropdownIndicator = (props) => {
      return (
        <div {...props.innerProps} className="px-2 opacity-50 cursor-pointer">
          <ChevronDown className="h-4 w-4" />
        </div>
      );
    };

    // Custom Clear Indicator
    const ClearIndicator = (props) => {
      return (
        <div
          {...props.innerProps}
          className="px-2 opacity-50 cursor-pointer hover:opacity-100"
        >
          <X className="h-4 w-4" />
        </div>
      );
    };

    const MenuList = (props) => {
      return (
        <div onWheel={(e) => e.stopPropagation()}>
          <SelectComponents.MenuList {...props} />
        </div>
      );
    };

    return (
      <Select
        ref={ref}
        className={cn("w-full text-sm", className)}
        menuPortalTarget={
          props.menuPortalTarget ||
          (typeof document !== "undefined" ? document.body : null)
        }
        menuPosition="fixed"
        captureMenuScroll={false}
        menuShouldBlockScroll={false}
        styles={{
          menuPortal: (base) => ({
            ...base,
            zIndex: 9999,
            pointerEvents: "auto",
          }),
          menu: (base) => ({
            ...base,
            zIndex: 9999,
          }),
          menuList: (base) => ({
            ...base,
            maxHeight:
              typeof props.maxMenuHeight === "number"
                ? `${props.maxMenuHeight}px`
                : props.maxMenuHeight || "250px",
            overflowY: "auto",
          }),
        }}
        classNames={{
          control: (state) =>
            cn(
              "flex min-h-[40px] w-full items-center justify-between rounded-md border border-input bg-background px-3 py-1 text-sm shadow-sm placeholder:text-muted-foreground focus:outline-none disabled:cursor-not-allowed disabled:opacity-50 transition-all",
              state.isFocused ? "ring-1 ring-ring border-ring" : "",
              error ? "border-destructive text-destructive" : "",
              customClassNames?.control?.(state),
            ),
          valueContainer: (state) =>
            cn("flex-1 px-1 gap-1", customClassNames?.valueContainer?.(state)),
          placeholder: (state) =>
            cn(
              "text-muted-foreground text-sm",
              customClassNames?.placeholder?.(state),
            ),
          input: (state) =>
            cn("text-foreground text-sm", customClassNames?.input?.(state)),
          singleValue: (state) =>
            cn(
              "text-foreground text-sm",
              customClassNames?.singleValue?.(state),
            ),
          indicatorsContainer: (state) =>
            cn(
              "flex items-center",
              customClassNames?.indicatorsContainer?.(state),
            ),
          multiValue: (state) =>
            cn(
              "bg-secondary text-secondary-foreground rounded-sm m-0.5 px-1 py-0.5 flex items-center gap-1",
              customClassNames?.multiValue?.(state),
            ),
          multiValueLabel: (state) =>
            cn("text-xs px-1", customClassNames?.multiValueLabel?.(state)),
          multiValueRemove: (state) =>
            cn(
              "hover:bg-destructive hover:text-destructive-foreground rounded-sm cursor-pointer",
              customClassNames?.multiValueRemove?.(state),
            ),
          menu: (state) =>
            cn(
              "z-[9999] min-w-[8rem] overflow-hidden rounded-md border border-border bg-background text-foreground shadow-md animate-in fade-in-80 my-2 bg-white dark:bg-slate-950",
              customClassNames?.menu?.(state),
            ),
          menuList: (state) =>
            cn(
              "overflow-y-auto p-1 max-h-[inherit]",
              customClassNames?.menuList?.(state),
            ),
          group: (state) => cn("py-1", customClassNames?.group?.(state)),
          groupHeading: (state) =>
            cn(
              "py-1.5 px-2 text-[11px] font-semibold uppercase tracking-wider text-muted-foreground bg-muted/40 rounded-sm mb-1",
              customClassNames?.groupHeading?.(state),
            ),
          option: (state) =>
            cn(
              "relative flex w-full cursor-pointer select-none items-center rounded-sm py-2 px-2 text-sm outline-none transition-colors",
              state.isFocused && "bg-accent text-accent-foreground",
              state.isSelected && "bg-primary text-primary-foreground",
              customClassNames?.option?.(state),
            ),
          noOptionsMessage: (state) =>
            cn(
              "p-2 text-sm text-muted-foreground",
              customClassNames?.noOptionsMessage?.(state),
            ),
          loadingMessage: (state) =>
            cn(
              "p-2 text-sm text-muted-foreground",
              customClassNames?.loadingMessage?.(state),
            ),
        }}
        components={{
          DropdownIndicator,
          ClearIndicator,
          MenuList,
          IndicatorSeparator: () => null,
        }}
        placeholder={placeholder}
        options={options}
        value={value}
        onChange={onChange}
        isDisabled={isDisabled}
        unstyled={true}
        {...props}
      />
    );
  },
);

ReactSelect.displayName = "ReactSelect";

const AsyncReactSelect = React.forwardRef(
  (
    {
      className,
      placeholder,
      loadOptions,
      defaultOptions,
      value,
      onChange,
      isDisabled,
      error,
      classNames: customClassNames,
      ...props
    },
    ref,
  ) => {
    // Debounce loadOptions to prevent excessive API calls
    const debouncedLoadOptions = React.useMemo(() => {
      if (!loadOptions) return undefined;
      let timeoutId;
      return (inputValue, callback) => {
        return new Promise((resolve) => {
          clearTimeout(timeoutId);
          timeoutId = setTimeout(async () => {
            try {
              const result = await loadOptions(inputValue);
              resolve(result);
              if (callback) callback(result);
            } catch {
              resolve([]);
              if (callback) callback([]);
            }
          }, 400);
        });
      };
    }, [loadOptions]);

    const DropdownIndicator = (props) => {
      return (
        <div {...props.innerProps} className="px-2 opacity-50 cursor-pointer">
          <ChevronDown className="h-4 w-4" />
        </div>
      );
    };

    const ClearIndicator = (props) => {
      return (
        <div
          {...props.innerProps}
          className="px-2 opacity-50 cursor-pointer hover:opacity-100"
        >
          <X className="h-4 w-4" />
        </div>
      );
    };

    const MenuList = (props) => {
      return (
        <div onWheel={(e) => e.stopPropagation()}>
          <SelectComponents.MenuList {...props} />
        </div>
      );
    };

    return (
      <AsyncSelect
        ref={ref}
        className={cn("w-full text-sm", className)}
        menuPortalTarget={
          props.menuPortalTarget ||
          (typeof document !== "undefined" ? document.body : null)
        }
        menuPosition="fixed"
        captureMenuScroll={false}
        menuShouldBlockScroll={false}
        styles={{
          menuPortal: (base) => ({
            ...base,
            zIndex: 9999,
            pointerEvents: "auto",
          }),
          menu: (base) => ({
            ...base,
            zIndex: 9999,
          }),
          menuList: (base) => ({
            ...base,
            maxHeight:
              typeof props.maxMenuHeight === "number"
                ? `${props.maxMenuHeight}px`
                : props.maxMenuHeight || "250px",
            overflowY: "auto",
          }),
        }}
        classNames={{
          control: (state) =>
            cn(
              "flex min-h-[40px] w-full items-center justify-between rounded-md border border-input bg-background px-3 py-1 text-sm shadow-sm placeholder:text-muted-foreground focus:outline-none disabled:cursor-not-allowed disabled:opacity-50 transition-all",
              state.isFocused ? "ring-1 ring-ring border-ring" : "",
              error ? "border-destructive text-destructive" : "",
              customClassNames?.control?.(state),
            ),
          valueContainer: (state) =>
            cn("flex-1 px-1 gap-1", customClassNames?.valueContainer?.(state)),
          placeholder: (state) =>
            cn(
              "text-muted-foreground text-sm",
              customClassNames?.placeholder?.(state),
            ),
          input: (state) =>
            cn("text-foreground text-sm", customClassNames?.input?.(state)),
          singleValue: (state) =>
            cn(
              "text-foreground text-sm",
              customClassNames?.singleValue?.(state),
            ),
          indicatorsContainer: (state) =>
            cn(
              "flex items-center",
              customClassNames?.indicatorsContainer?.(state),
            ),
          multiValue: (state) =>
            cn(
              "bg-secondary text-secondary-foreground rounded-sm m-0.5 px-1 py-0.5 flex items-center gap-1",
              customClassNames?.multiValue?.(state),
            ),
          multiValueLabel: (state) =>
            cn("text-xs px-1", customClassNames?.multiValueLabel?.(state)),
          multiValueRemove: (state) =>
            cn(
              "hover:bg-destructive hover:text-destructive-foreground rounded-sm cursor-pointer",
              customClassNames?.multiValueRemove?.(state),
            ),
          menu: (state) =>
            cn(
              "z-[9999] min-w-[8rem] overflow-hidden rounded-md border border-border bg-background text-foreground shadow-md animate-in fade-in-80 my-2 bg-white dark:bg-slate-950",
              customClassNames?.menu?.(state),
            ),
          menuList: (state) =>
            cn(
              "overflow-y-auto p-1 max-h-[inherit]",
              customClassNames?.menuList?.(state),
            ),
          option: (state) =>
            cn(
              "relative flex w-full cursor-pointer select-none items-center rounded-sm py-2 px-2 text-sm outline-none transition-colors",
              state.isFocused && "bg-accent text-accent-foreground",
              state.isSelected && "bg-primary text-primary-foreground",
              customClassNames?.option?.(state),
            ),
          noOptionsMessage: (state) =>
            cn(
              "p-2 text-sm text-muted-foreground",
              customClassNames?.noOptionsMessage?.(state),
            ),
          loadingMessage: (state) =>
            cn(
              "p-2 text-sm text-muted-foreground",
              customClassNames?.loadingMessage?.(state),
            ),
        }}
        components={{
          DropdownIndicator,
          ClearIndicator,
          MenuList,
          IndicatorSeparator: () => null,
        }}
        placeholder={placeholder}
        loadOptions={debouncedLoadOptions}
        defaultOptions={defaultOptions}
        value={value}
        onChange={onChange}
        isDisabled={isDisabled}
        unstyled={true}
        {...props}
      />
    );
  },
);

AsyncReactSelect.displayName = "AsyncReactSelect";

const CreatableReactSelect = React.forwardRef(
  (
    {
      className,
      placeholder,
      options,
      value,
      onChange,
      isDisabled,
      error,
      classNames: customClassNames,
      ...props
    },
    ref,
  ) => {
    const DropdownIndicator = (indicatorProps) => {
      return (
        <div {...indicatorProps.innerProps} className="px-2 opacity-50 cursor-pointer">
          <ChevronDown className="h-4 w-4" />
        </div>
      );
    };

    const ClearIndicator = (clearProps) => {
      return (
        <div
          {...clearProps.innerProps}
          className="px-2 opacity-50 cursor-pointer hover:opacity-100"
        >
          <X className="h-4 w-4" />
        </div>
      );
    };

    const MenuList = (menuListProps) => {
      return (
        <div onWheel={(e) => e.stopPropagation()}>
          <SelectComponents.MenuList {...menuListProps} />
        </div>
      );
    };

    return (
      <CreatableSelect
        ref={ref}
        className={cn("w-full text-sm", className)}
        menuPortalTarget={
          props.menuPortalTarget ||
          (typeof document !== "undefined" ? document.body : null)
        }
        menuPosition="fixed"
        captureMenuScroll={false}
        menuShouldBlockScroll={false}
        styles={{
          menuPortal: (base) => ({
            ...base,
            zIndex: 9999,
            pointerEvents: "auto",
          }),
          menu: (base) => ({
            ...base,
            zIndex: 9999,
          }),
          menuList: (base) => ({
            ...base,
            maxHeight:
              typeof props.maxMenuHeight === "number"
                ? `${props.maxMenuHeight}px`
                : props.maxMenuHeight || "250px",
            overflowY: "auto",
          }),
        }}
        classNames={{
          control: (state) =>
            cn(
              "flex min-h-[40px] w-full items-center justify-between rounded-md border border-input bg-background px-3 py-1 text-sm shadow-sm placeholder:text-muted-foreground focus:outline-none disabled:cursor-not-allowed disabled:opacity-50 transition-all",
              state.isFocused ? "ring-1 ring-ring border-ring" : "",
              error ? "border-destructive text-destructive" : "",
              customClassNames?.control?.(state),
            ),
          valueContainer: (state) =>
            cn("flex-1 px-1 gap-1", customClassNames?.valueContainer?.(state)),
          placeholder: (state) =>
            cn(
              "text-muted-foreground text-sm",
              customClassNames?.placeholder?.(state),
            ),
          input: (state) =>
            cn("text-foreground text-sm", customClassNames?.input?.(state)),
          singleValue: (state) =>
            cn(
              "text-foreground text-sm",
              customClassNames?.singleValue?.(state),
            ),
          indicatorsContainer: (state) =>
            cn(
              "flex items-center",
              customClassNames?.indicatorsContainer?.(state),
            ),
          multiValue: (state) =>
            cn(
              "bg-secondary text-secondary-foreground rounded-sm m-0.5 px-1 py-0.5 flex items-center gap-1",
              customClassNames?.multiValue?.(state),
            ),
          multiValueLabel: (state) =>
            cn("text-xs px-1", customClassNames?.multiValueLabel?.(state)),
          multiValueRemove: (state) =>
            cn(
              "hover:bg-destructive hover:text-destructive-foreground rounded-sm cursor-pointer",
              customClassNames?.multiValueRemove?.(state),
            ),
          menu: (state) =>
            cn(
              "z-[9999] min-w-[8rem] overflow-hidden rounded-md border border-border bg-background text-foreground shadow-md animate-in fade-in-80 my-2 bg-white dark:bg-slate-950",
              customClassNames?.menu?.(state),
            ),
          menuList: (state) =>
            cn(
              "overflow-y-auto p-1 max-h-[inherit]",
              customClassNames?.menuList?.(state),
            ),
          option: (state) =>
            cn(
              "relative flex w-full cursor-pointer select-none items-center rounded-sm py-2 px-2 text-sm outline-none transition-colors",
              state.isFocused && "bg-accent text-accent-foreground",
              state.isSelected && "bg-primary text-primary-foreground",
              customClassNames?.option?.(state),
            ),
          noOptionsMessage: (state) =>
            cn(
              "p-2 text-sm text-muted-foreground",
              customClassNames?.noOptionsMessage?.(state),
            ),
          loadingMessage: (state) =>
            cn(
              "p-2 text-sm text-muted-foreground",
              customClassNames?.loadingMessage?.(state),
            ),
        }}
        components={{
          DropdownIndicator,
          ClearIndicator,
          MenuList,
          IndicatorSeparator: () => null,
        }}
        placeholder={placeholder}
        options={options}
        value={value}
        onChange={onChange}
        isDisabled={isDisabled}
        unstyled={true}
        {...props}
      />
    );
  },
);

CreatableReactSelect.displayName = "CreatableReactSelect";

export { ReactSelect, AsyncReactSelect, CreatableReactSelect };
export default ReactSelect;

